import React, { useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LayoutGrid, ListCollapse } from "lucide-react";
import CustomTooltip from "../Tooltip/CustomTooltip";
import AnchoredPanel from "../shared/AnchoredPanel";
import SidebarToggler1 from "../Sidebar/SidebarToggler";
import { HeaderForgeLogo, HeaderSpectraLogo } from "../HeaderComponents";
import { SparkDarkSvg, SparkSvg } from "../../assets";
import UserDropdown from "../Dropdowns/UserDropdown";
import NotificationBell from "../Notifications/NotificationBell";
import HeaderAvatarStack from "./HeaderAvatarStack";
import { useTheme } from "../../context/ThemeProvider";
import { SunSvg, MoonSvg } from "../../assets";
import UIStore from "../../store/uiStore";

const Header = ({
  type,
  space_id,
  chat_id,
  spaceName,
  collaborators = [],
  hasArtifacts = false,
  compactAnswers = true,
  onCompactAnswersChange,
}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { theme, colorScheme, toggleTheme } = useTheme();
  const showSidebar = UIStore((state) => state.showSidebar);
  const toggleSidebar = UIStore((state) => state.toggleSidebar);
  const setShowSidebar = UIStore((state) => state.setShowSidebar);
  const [answerMenuOpen, setAnswerMenuOpen] = useState(false);
  const answerMenuButtonRef = useRef(null);

  const isNebulaMode = searchParams.get("mode") === "nebula";

  const toggleNebulaMode = () => {
    // replace: true — Nebula on/off is a UI mode toggle, not a page navigation.
    // Without it, every toggle pushed a new history entry, so a few Nebula
    // open/close cycles during one chat session meant the browser Back button
    // just replayed old toggle states instead of leaving the chat.
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (isNebulaMode) {
        next.delete("mode");
      } else {
        next.set("mode", "nebula");
      }
      return next;
    }, { replace: true });
  };

  const renderLogo = () => {
    switch (type) {
      case "forge":
        return <HeaderForgeLogo />;
      case "spectra-dashboard":
      case "spectra-chat":
        return <HeaderSpectraLogo spaceName={spaceName} />;
      case "spark-dashboard":
      case "spark-chat":
        return (
          <button
            onClick={() => {
              navigate("/spark");
              // Close sidebar on mobile/tablet after navigation
              if (window.innerWidth < 940) {
                setShowSidebar(false);
              }
            }}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            {colorScheme === "docs" ? <SparkSvg className="photon-navigation-icon photon-product-icon text-ink-primary" aria-hidden="true" /> : theme === "dark" ? <SparkDarkSvg className="photon-navigation-icon" /> : <SparkSvg className="photon-navigation-icon" />}
            <span className="hidden sm:inline text-lg font-semibold text-ink-ink dark:text-ink-white ml-2">
              Spark
            </span>
          </button>
        );
      default:
        return null;
    }
  };

  const showCollaborators = type === "spectra-chat" && collaborators.length > 1;

  return (
    <header className="md:border-b border-edge-border dark:border-edge-muted-control sticky top-0 z-50">
      <div className="flex items-center justify-between h-14 sm:h-16 px-2 sm:px-6">
        {/* Left Section */}
        <div className="flex min-w-0 items-center gap-1">
          <SidebarToggler1
            showSidebar={showSidebar}
            toggleSidebar={toggleSidebar}
            theme={theme}
            className="pr-1 mr-1 sm:pr-4 sm:mr-4"
          />
          {renderLogo()}
        </div>

        {/* Right Section */}
        <div className="flex shrink-0 items-center gap-0.5 sm:gap-3">
          {(type === "spark-chat" || type === "spectra-chat") && (
            <CustomTooltip content={isNebulaMode ? "Exit Nebula" : "Open artifacts in Nebula"} side="bottom">
              <button
                type="button"
                onClick={toggleNebulaMode}
                className={`photon-header-control relative flex items-center justify-center sm:justify-start sm:gap-1.5 w-8 sm:w-auto h-8 sm:px-3 rounded-lg text-[13px] font-medium transition-colors ${
                  `border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fill-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-page ${isNebulaMode
                      ? "border-edge-canvas-accent/30 bg-fill-primary/10 text-ink-primary"
                      : "border-edge-border text-ink-gray-500 hover:bg-fill-gray-100 hover:text-ink-primary"}`
                }`}
                aria-label={isNebulaMode ? "Exit Nebula" : "Open Nebula"}
                aria-pressed={isNebulaMode}
              >
                <LayoutGrid size={14} />
                <span className="hidden sm:inline">Nebula</span>
                {!isNebulaMode && hasArtifacts && (
                  <span className="hidden sm:block w-1.5 h-1.5 rounded-full bg-fill-canvas-accent ml-0.5" />
                )}
              </button>
            </CustomTooltip>
          )}
          {(type === "spark-chat" || type === "spectra-chat") && !isNebulaMode && (
            <>
              <CustomTooltip content="Answer display" side="bottom">
                <button
                  ref={answerMenuButtonRef}
                  onClick={() => setAnswerMenuOpen((open) => !open)}
                  className={`flex items-center justify-center w-8 h-8 rounded-lg transition-colors ${
                    compactAnswers
                      ? "text-ink-editor-accent dark:text-ink-editor-accent-dark sm:border sm:border-edge-canvas-accent sm:bg-fill-canvas-surface sm:dark:bg-fill-canvas-surface-dark"
                      : "text-ink-muted-ink dark:text-ink-gray-400 sm:border sm:border-edge-gray-300 sm:dark:border-edge-canvas-border sm:hover:bg-fill-gray-100 sm:dark:hover:bg-fill-canvas-hover"
                  }`}
                  aria-label="Answer display options"
                  aria-pressed={compactAnswers}
                  aria-expanded={answerMenuOpen}
                >
                  <ListCollapse size={15} />
                </button>
              </CustomTooltip>
              <AnchoredPanel
                anchorRef={answerMenuButtonRef}
                isOpen={answerMenuOpen}
                onClose={() => setAnswerMenuOpen(false)}
                width={250}
                align="right"
                className="p-2"
              >
                <button
                  onClick={() => onCompactAnswersChange?.(!compactAnswers)}
                  className="w-full flex items-center justify-between gap-3 px-2 py-2 rounded-md text-left hover:bg-fill-gray-100 dark:hover:bg-fill-gray-700"
                  role="switch"
                  aria-checked={compactAnswers}
                >
                  <span>
                    <span className="block text-xs font-semibold text-ink-ink dark:text-ink-white">Collapse Sparks</span>
                    <span className="block mt-0.5 text-[10px] leading-4 text-ink-gray-500 dark:text-ink-gray-400">Show previews for long Sparks earlier in the conversation.</span>
                  </span>
                  <span className={`relative w-8 h-[18px] rounded-full shrink-0 transition-colors ${compactAnswers ? "bg-fill-canvas-accent" : "bg-fill-gray-300 dark:bg-fill-gray-600"}`}>
                    <span className={`absolute top-[3px] w-3 h-3 rounded-full bg-fill-white shadow transition-transform ${compactAnswers ? "translate-x-[17px]" : "translate-x-[3px]"}`} />
                  </span>
                </button>
              </AnchoredPanel>
            </>
          )}
          <NotificationBell />
          {/* Appearance toggle */}
          <button
            type="button"
            onClick={toggleTheme}
            aria-label={theme === "dark" ? "Switch to Light" : "Switch to Dark"}
            title={theme === "dark" ? "Switch to Light" : "Switch to Dark"}
            className="bg-fill-gray-100 dark:bg-fill-gray-700 border border-edge-gray-200 dark:border-edge-panel-border shadow-sm px-1 w-11 h-6 rounded-full relative cursor-pointer flex justify-between items-center transition-colors duration-200 shrink-0"
          >
            <SunSvg className="photon-appearance-icon text-ink-primary dark:opacity-50 text-[14px]" />
            <MoonSvg className="photon-appearance-icon text-ink-gray-500 dark:text-ink-accent text-[14px]" />
            <span aria-hidden="true"
              className={`absolute w-4 h-4 rounded-full bg-fill-white dark:bg-fill-gray-200 shadow-md transition-transform duration-300 ease-in-out ${
                theme === "dark" ? "translate-x-[18px]" : "translate-x-0"
              }`}
            />
          </button>
          {showCollaborators ? (
            <HeaderAvatarStack collaborators={collaborators} />
          ) : (
            <UserDropdown theme={theme} />
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
